import { existsSync, readFileSync } from 'node:fs';
import { join } from 'node:path';
import { parseYaml } from './config-as-code/yaml.js';
import { isNewerVersion } from './version.js';

// Raw published SKILL.md — path must match the layout written by
// `.github/workflows/sync-public-skill.yml` (skills/rekor/ on the public mirror).
export const DEFAULT_SKILL_RAW_URL =
  'https://raw.githubusercontent.com/wayai-pro/rekor-skill/main/skills/rekor/SKILL.md';

/**
 * The one-liner that installs the rekor skill across supported AI agents.
 *
 * `mkdir -p .claude` is load-bearing, not cosmetic: the external `skills` CLI (`npx skills add`)
 * writes the real skill to `.agents/skills/rekor` and symlinks `.claude/skills/rekor` to it — but
 * creates that symlink ONLY when `.claude/` already exists, silently skipping it otherwise while
 * still reporting success. Claude Code reads `.claude/skills/`, so without the guard a fresh repo
 * never loads the skill. The CLI also self-heals the link at runtime (see `skill-symlink.ts`); this
 * keeps the *documented* command correct on first install too.
 */
export const SKILL_INSTALL_CMD = 'mkdir -p .claude && npx skills add wayai-pro/rekor-skill -y';

// Where `SKILL_INSTALL_CMD` drops the skill across supported agents.
export const SKILL_INSTALL_PATHS = [
  '.claude/skills/rekor/SKILL.md',
  '.opencode/skills/rekor/SKILL.md',
  '.agents/skills/rekor/SKILL.md',
] as const;

// Per-harness skill roots (a skill lands at `<harness>/skills/<name>/`). Shared by the public-skill
// detection above and `rekor admin skill install` (which writes private operator skills here).
export const HARNESS_SKILL_DIRS = ['.claude', '.opencode', '.agents'] as const;

export interface InstalledSkill {
  path: string;
  version: string | null;
}

/** Read the `version` field from a SKILL.md YAML frontmatter block, or null if absent/malformed. */
export function parseFrontmatterVersion(content: string): string | null {
  const match = /^---\r?\n([\s\S]*?)\r?\n---(?:\r?\n|$)/.exec(content);
  const front = match?.[1];
  if (front === undefined) return null;
  let parsed: unknown;
  try {
    parsed = parseYaml(front);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object') return null;
  const v = (parsed as Record<string, unknown>).version;
  return typeof v === 'string' ? v : null;
}

/** Installed rekor skill copies under `projectRoot`, with their frontmatter versions. */
export function findInstalledSkills(projectRoot: string): InstalledSkill[] {
  const found: InstalledSkill[] = [];
  for (const rel of SKILL_INSTALL_PATHS) {
    const path = join(projectRoot, rel);
    if (!existsSync(path)) continue;
    let version: string | null = null;
    try {
      version = parseFrontmatterVersion(readFileSync(path, 'utf-8'));
    } catch {
      // Unreadable — treat as version-unknown.
    }
    found.push({ path, version });
  }
  return found;
}

/** Lowest installed version across copies, so the nudge fires if any copy is behind. */
export function lowestInstalledVersion(installs: InstalledSkill[]): string | null {
  const versions = installs.map((i) => i.version).filter((v): v is string => v !== null);
  if (versions.length === 0) return null;
  return versions.reduce((lowest, v) => (isNewerVersion(lowest, v) ? v : lowest));
}

/**
 * Fetch the published SKILL.md and parse its frontmatter version, or null on any failure (non-ok
 * response, network error, malformed frontmatter). Bounded by an abort timeout. Used ONLY by the
 * detached background refresher (`version-refresh.ts`) — never on the foreground path, so its
 * latency is never charged to a user command.
 */
export async function fetchLatestSkillVersion(timeoutMs: number): Promise<string | null> {
  const url = process.env['REKOR_SKILL_RAW_URL'] || DEFAULT_SKILL_RAW_URL;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(url, { signal: controller.signal });
    if (!res.ok) return null;
    return parseFrontmatterVersion(await res.text());
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}
